import { Loader } from "@/components/Loader";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  categoryIndicators,
  formatRiskPointChange,
  mapRiskName,
  type RiskIndicator,
} from "@/revenue-intelligence/lib/risk-contracts";
import { cn } from "@/lib/utils";
import { getDealOwner, getUserIdsForTeam } from "@revelate/utils";
import { getFullName } from "@revelate/utils";
import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  CircleAlert,
  Sparkles,
} from "lucide-react";
import { lazy, Suspense, useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { AtRiskDealsDataTable } from "../components/AtRiskDealsDataTable";
import {
  RevenueIntelligenceChatRegistration,
} from "../components/RevenueIntelligenceChatRegistration";
import { RevenueIntelligenceChatScopeRegistration } from "../components/RevenueIntelligenceChatScopeRegistration";
import {
  defineRevenueIntelligenceAnalysisContract,
  getChatRegistrationFromAnalysisContract,
} from "../lib/revenue-intelligence-analysis-contract";
import { useAppStore } from "@/stores";
import {
  getDealTopRiskInCateogory,
  getManagementReviewUrgencyRisks,
} from "../lib/risk";
import {
  ForecastChart,
  ForecastChartRow,
  ForecastPosition,
} from "./ForecastChart";
import {
  AnalysisNarrative,
  InsightSectionHeader,
} from "./AnalysisNarrative";
import { ForecastFilters } from "./ForecastFilters";
import { ForecastMovementWaterfall } from "./ForecastMovementWaterfall";
import { ForecastConcentrationCard } from "./ForecastConcentrationCard";
import {
  formatForecastShare,
  getCurrentPositionCopy,
  getForecastDealEvidenceCopy,
  getForecastExecutiveCopy,
  getForecastManagementActionCopy,
  getForecastManagementActionSupportingCopy,
  getForecastManagementCopy,
  getForecastMovementCopy,
  getForecastMovementCategoryCopy,
  getForecastMovementDriverCopy,
  getForecastSignalsCopy,
} from "./forecastInsightCopy";
import {
  buildForecastCurrentSignalEvidence,
  buildForecastL2Insights,
  buildForecastLossPatternEvidence,
  getDealForecastContribution,
  getDealsForForecastSignal,
  getTopForecastConcentration,
  getTopForecastMembershipConcentration,
  type ForecastConcentration,
  type ForecastCurrentSignalEvidence,
  type ForecastLossPatternEvidence,
  type ForecastL2Insights,
  type ForecastManagementAction,
  type ForecastMovementCategoryInsight,
} from "./forecastInsights";
import {
  PipelineHealthOverviewCard,
  PipelineRiskOverviewCard,
} from "./PipelineOverviewCards";
import { formatCompactIsoCurrency } from "@/revenue-intelligence/lib/formatCurrency";
import { getStateOfSalesV2ForecastPosition } from "./forecastPosition";
import { stateOfSalesV2Colors } from "./visualTokens";
import { useStateOfSalesV2PipelineData } from "./usePipelineData";
import {
  buildStateOfSalesV2Href,
  buildStateOfSalesV2Query,
  useStateOfSalesV2Forecast,
} from "./useForecastData";
import {
  buildStateOfSalesForecastChatMetrics,
  buildStateOfSalesForecastTimelineChatDataset,
  buildStateOfSalesPipelineRiskChatContract,
} from "./stateOfSalesChatContracts";
import {
  buildStateOfSalesChatScopeActions,
  getStateOfSalesChatFiltersLabel,
} from "./stateOfSalesChatScope";
import { GrowthEfficiencyOverviewCard } from "./GrowthEfficiencyOverviewCard";
import { getStateOfSalesL1LoadState } from "./stateOfSalesLoading";
import {
  buildForecastOverviewChatContract,
  getForecastSummary,
} from "./forecastOverviewChatContract";
import { applyExpectedCloseV2DealContributions } from "../expected-close-v2/localExpectedCloseForecast";

type ForecastData = NonNullable<ReturnType<typeof useStateOfSalesV2Forecast>["forecast"]>;
type ForecastDeal = ForecastData["stateOfSalesV2OpenDeals"][number];
type ForecastLossSignalGroup = ForecastData["stateOfSalesV2LossSignalGroups"][number];

const GrowthEfficiencyOverviewDataCard = lazy(() => import(
  "./GrowthEfficiencyOverviewDataCard"
).then(module => ({ default: module.GrowthEfficiencyOverviewDataCard })));

function ForecastLoadingShell({ detail = false }: { detail?: boolean }) {
  return (
    <div className="space-y-5 pb-8" aria-busy="true" aria-label="Loading Forecast">
      <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
        <div className="h-8 w-44 animate-pulse rounded bg-gray-200" />
        <div className="h-10 w-full animate-pulse rounded bg-gray-100 lg:w-96" />
      </div>
      <div className="h-24 animate-pulse rounded-xl border border-gray-200 bg-gray-50" />
      <div className="grid gap-3 lg:grid-cols-3">
        <div className="flex h-[420px] items-center justify-center rounded-xl border border-gray-200 bg-gray-50 lg:col-span-2">
          <Loader size={32} parentClassName="" />
        </div>
        <div className="flex h-[420px] items-center justify-center rounded-xl border border-gray-200 bg-gray-50">
          <Loader size={32} parentClassName="" />
        </div>
      </div>
      {detail && (
        <div className="grid gap-3 lg:grid-cols-2">
          <div className="flex h-56 items-center justify-center rounded-xl border border-gray-200 bg-gray-50">
            <Loader size={24} parentClassName="" />
          </div>
          <div className="flex h-56 items-center justify-center rounded-xl border border-gray-200 bg-gray-50">
            <Loader size={24} parentClassName="" />
          </div>
        </div>
      )}
    </div>
  );
}

function useLoadAfterForecastPaint(ready: boolean) {
  const [load, setLoad] = useState(false);
  useEffect(() => {
    if (!ready) return;
    let timeoutId: number | undefined;
    const frameId = window.requestAnimationFrame(() => {
      timeoutId = window.setTimeout(() => setLoad(true), 0);
    });
    return () => {
      window.cancelAnimationFrame(frameId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [ready]);
  return ready && load;
}

function ForecastUnavailable({ retry }: { retry: () => Promise<unknown> }) {
  return (
    <Card className="border-dashed">
      <CardContent className="flex min-h-64 flex-col items-center justify-center gap-3 text-center">
        <CircleAlert className="h-5 w-5 text-gray-400" />
        <div>
          <p className="text-sm font-medium">Forecast data could not be loaded</p>
          <p className="mt-1 text-xs text-gray-500">A required forecast source could not be reached. Check the data connection and try again.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void retry()}>Try again</Button>
      </CardContent>
    </Card>
  );
}

function LossSignalCard({
  evidence,
  onClick,
}: {
  evidence: ForecastLossPatternEvidence;
  onClick: () => void;
}) {
  const hasDeals = evidence.affectedDealCount > 0;

  return (
    <button
      type="button"
      disabled={!hasDeals}
      onClick={onClick}
      aria-label={hasDeals ? `Open ${evidence.label} recent lost deals` : evidence.label}
      className="rounded-[8px] border border-gray-200 bg-white p-4 text-left transition-colors enabled:hover:border-[#9F86C0] enabled:hover:bg-[#9F86C0]/5 disabled:cursor-default"
    >
      <span className="block text-sm font-medium text-[#231942]">{evidence.label}</span>
      <span className="mt-2 block text-base font-medium tabular-nums text-[#231942]">
        {hasDeals
          ? `${evidence.affectedDealCount} of ${evidence.recentLostDealCount}`
          : "—"}
      </span>
      <span className="mt-1 block text-[11px] leading-4 text-gray-500">
        {hasDeals
          ? "recent lost deals"
          : "No recent lost deals"}
      </span>
      {hasDeals && (
        <span className="mt-2 block text-[11px] leading-4 text-gray-500">
          {evidence.openDealCount} of {evidence.openDealTotal} selected-period open deals
        </span>
      )}
    </button>
  );
}

function CurrentSignalCard({
  companyCurrency,
  evidence,
  onClick,
}: {
  companyCurrency: ReturnType<typeof useStateOfSalesV2Forecast>["companyCurrency"];
  evidence: ForecastCurrentSignalEvidence<ForecastDeal>;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={`Open ${evidence.label} deals`}
      onClick={onClick}
      className="rounded-[8px] border border-gray-200 bg-white p-4 text-left transition-colors hover:border-[#9F86C0] hover:bg-[#9F86C0]/5"
    >
      <span className="block text-sm font-medium text-[#231942]">{evidence.label}</span>
      <span className="mt-2 block text-base font-medium tabular-nums text-[#231942]">
        {formatCompactIsoCurrency(evidence.dealValue, companyCurrency)}
      </span>
      <span className="mt-1 block text-[11px] leading-4 text-gray-500">
        Deal value · {evidence.affectedDealCount} {evidence.affectedDealCount === 1 ? "open deal" : "open deals"}
      </span>
      {evidence.topDealName && (
        <span className="mt-2 block text-[11px] leading-4 text-gray-500">
          Largest deal: {evidence.topDealName}
        </span>
      )}
    </button>
  );
}

function ForecastMovementFacts({
  companyCurrency,
  insights,
}: {
  companyCurrency: ReturnType<typeof useStateOfSalesV2Forecast>["companyCurrency"];
  insights: ForecastL2Insights<ForecastDeal>;
}) {
  const movement = insights.movement;
  const facts = [
    {
      label: "Positive movement",
      value: movement.state === "insufficient_history"
        ? "—"
        : formatCompactIsoCurrency(movement.positiveMovement, companyCurrency),
    },
    {
      label: "Negative movement",
      value: movement.state === "insufficient_history"
        ? "—"
        : formatCompactIsoCurrency(movement.negativeMovement, companyCurrency),
    },
    {
      label: "Largest positive",
      value: movement.dominantPositiveDriver
        ? `${movement.dominantPositiveDriver.label} · ${formatCompactIsoCurrency(movement.dominantPositiveDriver.amount, companyCurrency)}`
        : "—",
    },
    {
      label: "Largest negative",
      value: movement.dominantNegativeDriver
        ? `${movement.dominantNegativeDriver.label} · ${formatCompactIsoCurrency(Math.abs(movement.dominantNegativeDriver.amount), companyCurrency)}`
        : "—",
    },
  ];

  return (
    <div className="grid gap-px overflow-hidden rounded-[8px] border border-gray-200 bg-gray-200 sm:grid-cols-2 xl:grid-cols-4">
      {facts.map(fact => (
        <div key={fact.label} className="bg-white px-4 py-3">
          <p className="text-[11px] text-gray-500">{fact.label}</p>
          <p className="mt-1 text-sm font-medium tabular-nums text-[#231942]">{fact.value}</p>
        </div>
      ))}
    </div>
  );
}

function MovementDriverCard({
  category,
  companyCurrency,
  onClickDeal,
}: {
  category: ForecastMovementCategoryInsight;
  companyCurrency: ReturnType<typeof useStateOfSalesV2Forecast>["companyCurrency"];
  onClickDeal: (dealId: number | string) => void;
}) {
  const copy = getForecastMovementCategoryCopy(category, companyCurrency);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="space-y-1 border-b border-gray-100 px-4 py-3">
        <CardDescription className="text-[11px]">{copy.label}</CardDescription>
        <CardTitle className="text-sm font-medium tabular-nums text-[#231942]">
          {copy.heading}
        </CardTitle>
        {copy.supporting && (
          <CardDescription className="text-[11px] leading-4">
            {copy.supporting}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {category.deals.length ? (
          category.deals.slice(0, 5).map(deal => (
            <button
              key={deal.id}
              type="button"
              onClick={() => onClickDeal(deal.id)}
              className="group flex w-full items-start justify-between gap-4 border-b border-gray-100 px-4 py-3 text-left last:border-b-0 hover:bg-[#9F86C0]/5"
            >
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-[#231942] group-hover:underline">
                  {deal.name}
                </span>
                <span className="mt-1 block text-[11px] leading-4 text-gray-500">
                  {deal.changedIndicators.length
                    ? deal.changedIndicators.map(change => change.label).join(" · ")
                    : "No supported indicator change is available for this period."}
                </span>
              </span>
              <span className="shrink-0 text-right">
                <span className="block text-sm font-medium tabular-nums text-[#231942]">
                  {deal.forecastImpact > 0 ? "+" : ""}{formatCompactIsoCurrency(deal.forecastImpact, companyCurrency)}
                </span>
                <span className="mt-1 block text-[11px] tabular-nums text-gray-500">
                  {deal.previousRisk !== null && deal.currentRisk !== null
                    ? `Risk ${formatRiskPointChange(deal.previousRisk, deal.currentRisk)}`
                    : "Risk change unavailable"}
                </span>
              </span>
            </button>
          ))
        ) : (
          <p className="px-4 py-3 text-sm text-gray-500">
            No deal-level details are available for this driver.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function ManagementActionRow({
  action,
  companyCurrency,
  onClick,
}: {
  action: ForecastManagementAction<ForecastDeal>;
  companyCurrency: ReturnType<typeof useStateOfSalesV2Forecast>["companyCurrency"];
  onClick: () => void;
}) {
  const supportingCopy = getForecastManagementActionSupportingCopy(action, companyCurrency);
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-start justify-between gap-4 border-b border-gray-100 px-4 py-3 text-left last:border-b-0 hover:bg-[#9F86C0]/5"
    >
      <span className="min-w-0">
        <span className="block text-sm font-medium leading-5 text-[#231942]">
          {getForecastManagementActionCopy(action, companyCurrency)}
        </span>
        {supportingCopy && (
          <span className="mt-1 block text-xs leading-4 text-gray-500">
            {supportingCopy}
          </span>
        )}
      </span>
      <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5 group-hover:text-[#5E548E]" />
    </button>
  );
}

export function RevenueIntelligenceStateOfSalesV2Page() {
  const model = useStateOfSalesV2Forecast();
  const pipeline = useStateOfSalesV2PipelineData(model);
  const currentUser = useAppStore(state => state.currentUser);
  const [, setLocation] = useLocation();
  const loadSalesEconomics = useLoadAfterForecastPaint(Boolean(
    model.forecast && !model.isLoading && !model.error,
  ));

  const loadState = getStateOfSalesL1LoadState({
    forecastAvailable: Boolean(model.forecast),
    modelError: model.error,
    modelLoading: model.isLoading,
  });
  if (loadState === "loading") return <ForecastLoadingShell />;
  if (loadState === "error" || !model.forecast) {
    return <ForecastUnavailable retry={model.retry} />;
  }

  const forecastPosition = getStateOfSalesV2ForecastPosition(
    model.forecast,
    model.period,
    model.accumulated,
    model.chartData,
  );
  const summary = getForecastSummary(
    model.forecast,
    forecastPosition,
    model.companyCurrency,
    !model.expectedCloseV2Available,
  );
  const forecastOverviewChatContract = buildForecastOverviewChatContract({
    accumulated: model.accumulated,
    chartData: model.chartData as ForecastChartRow[],
    companyCurrency: model.companyCurrency,
    companyId: String(currentUser?.company_id ?? "unavailable"),
    filtersLabel: getStateOfSalesChatFiltersLabel(model),
    forecast: model.forecast,
    period: model.period,
    movementComparable: !model.expectedCloseV2Available,
  });
  const pipelineRiskChatContract = pipeline.risk
    ? buildStateOfSalesPipelineRiskChatContract({
      analysis: pipeline.risk,
      companyCurrency: model.companyCurrency,
      companyId: String(currentUser?.company_id ?? "unavailable"),
      movement: pipeline.movement,
      pageLabel: "State of Sales",
      periodLabel: model.period,
      filtersLabel: getStateOfSalesChatFiltersLabel(model),
      selectedPeriodRange: pipeline.selectedPeriodRange,
      totalForecast: forecastPosition.forecast,
      urgencyDeals: pipeline.urgencyDeals,
    })
    : null;
  const resolveChatContracts = (filters: typeof model.filters) => {
    const scoped = pipeline.resolveForFilters(filters);
    if (!scoped) return [];
    const filtersLabel = getStateOfSalesChatFiltersLabel({
      ...model,
      selectedOwners: filters.owners,
      selectedPipelines: filters.pipelines,
      selectedTeams: filters.teams,
    });
    const scopedForecastContract = buildForecastOverviewChatContract({
      accumulated: filters.accumulated,
      chartData: model.buildChartDataForFilters(
        scoped.forecast,
        filters,
      ) as ForecastChartRow[],
      companyCurrency: model.companyCurrency,
      companyId: String(currentUser?.company_id ?? "unavailable"),
      filtersLabel,
      forecast: scoped.forecast,
      period: filters.period,
      movementComparable: !model.expectedCloseV2Available,
    });
    const scopedRiskContract = buildStateOfSalesPipelineRiskChatContract({
      analysis: scoped.risk,
      companyCurrency: model.companyCurrency,
      companyId: String(currentUser?.company_id ?? "unavailable"),
      movement: scoped.movement,
      pageLabel: "State of Sales",
      periodLabel: filters.period,
      filtersLabel,
      selectedPeriodRange: scoped.selectedPeriodRange,
      totalForecast: scoped.forecastPosition.forecast,
      urgencyDeals: scoped.urgencyDeals,
    });
    return [scopedForecastContract, scopedRiskContract].flatMap(contract => {
      const registration = getChatRegistrationFromAnalysisContract(contract);
      return registration ? [registration] : [];
    });
  };
  const forecastHref = buildStateOfSalesV2Href("forecast", model.filters);
  const pipelineRiskHref = `/revenue-intelligence/state-of-sales-v2/pipeline-risk?${buildStateOfSalesV2Query(model.filters)}`;
  const pipelineHealthHref = `/revenue-intelligence/state-of-sales-v2/pipeline-health?${buildStateOfSalesV2Query(model.filters)}`;
  const movementIsPositive = summary.movement !== null && summary.movement >= 0;
  const MovementIcon = movementIsPositive ? ArrowUpRight : ArrowDownRight;
  const context = buildStateOfSalesV2Query(model.filters);
  const growthEfficiencyContext = new URLSearchParams(context);
  growthEfficiencyContext.set("period", "last_90_days");
  growthEfficiencyContext.set("breakdown", "team");
  const growthEfficiencyHref = `/revenue-intelligence/state-of-sales-v2/sales-economics?${growthEfficiencyContext.toString()}`;
  const riskDealContext = new URLSearchParams(context);
  riskDealContext.set("dealDetailsViewSource", "state-of-sales-v2-pipeline-risk");
  const openPipelineRiskDeal = (dealId: number | string) => setLocation(
    `/revenue-intelligence/deal/${dealId}?${riskDealContext.toString()}`,
  );

  return (
    <div className="space-y-5 pb-8">
      <RevenueIntelligenceChatRegistration contract={forecastOverviewChatContract} />
      <RevenueIntelligenceChatScopeRegistration
        actions={buildStateOfSalesChatScopeActions(model, resolveChatContracts)}
        scopeKey="state_of_sales.filters"
      />
      {pipelineRiskChatContract && (
        <RevenueIntelligenceChatRegistration contract={pipelineRiskChatContract} />
      )}
      <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
        <div>
          <h1 className="text-2xl font-semibold">State of Sales</h1>
        </div>
        <ForecastFilters model={model} />
      </div>

      <AnalysisNarrative
        conclusion={summary.conclusion}
        explanation={summary.explanation}
        evidence={summary.evidence}
      />

      <div className="grid items-stretch gap-3 lg:grid-cols-3">
        <section aria-labelledby="forecast" className="lg:col-span-2">
          <Card
            className="h-full cursor-pointer border-[#1D819A]/40 bg-[#1D819A]/[0.025] transition-colors hover:border-[#1D819A]/70 hover:bg-[#1D819A]/[0.04]"
            data-testid="forecast-card"
            role="link"
            tabIndex={0}
            onClick={() => setLocation(forecastHref)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setLocation(forecastHref);
              }
            }}
          >
            <CardHeader className="p-5 pb-3">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <CardTitle id="forecast" className="text-base">Forecast</CardTitle>
                  {model.forecastEnrichmentLoading && (
                    <span className="text-xs text-gray-500">Updating Deal coverage…</span>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  {summary.movement !== null && (
                    <div
                      className="flex items-center gap-1 rounded-full px-2 py-1 text-xs tabular-nums"
                      style={{
                        backgroundColor: movementIsPositive
                          ? `${stateOfSalesV2Colors.positive}12`
                          : `${stateOfSalesV2Colors.negative}12`,
                        color: movementIsPositive
                          ? stateOfSalesV2Colors.positive
                          : stateOfSalesV2Colors.negative,
                      }}
                    >
                      <MovementIcon className="h-3.5 w-3.5" />
                      {formatCompactIsoCurrency(Math.abs(summary.movement), model.companyCurrency)}
                    </div>
                  )}
                  <Link
                    href={forecastHref}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-[#5E548E] hover:text-[#231942]"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    Open Forecast
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-5 pt-0">
              <ForecastChart
                accumulated={model.accumulated}
                data={model.chartData as ForecastChartRow[]}
                companyCurrency={model.companyCurrency}
                future={forecastPosition.future}
                historical={forecastPosition.historical}
                onAccumulatedChange={model.setAccumulated}
                selectedPeriodStart={model.selectedPeriodStart}
                position={forecastPosition}
              />
            </CardContent>
          </Card>
        </section>
        <section aria-label="Pipeline Risk">
          <PipelineRiskOverviewCard
            companyCurrency={model.companyCurrency}
            error={pipeline.error}
            href={pipelineRiskHref}
            isLoading={pipeline.isLoading}
            open={() => setLocation(pipelineRiskHref)}
            openDeal={(deal) => openPipelineRiskDeal(deal.id)}
            risk={pipeline.risk}
            selectedPeriodOpenDeals={pipeline.selectedPeriodOpenDeals}
            urgencyDeals={pipeline.urgencyDeals}
          />
        </section>
      </div>

      <section aria-labelledby="pipeline-health" className="grid gap-3 lg:grid-cols-3">
        <div className="flex items-end justify-between gap-4 lg:col-span-2">
          <h2 id="pipeline-health" className="text-base font-semibold">Pipeline Health</h2>
          <Link
            href={pipelineHealthHref}
            className="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-[#5E548E] hover:text-[#231942]"
          >
            Open Pipeline Health
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="hidden lg:block" />
        <div className="lg:col-span-2">
          <PipelineHealthOverviewCard
            error={pipeline.error}
            health={pipeline.health}
            historical={forecastPosition.historical}
            href={pipelineHealthHref}
            isLoading={pipeline.isLoading}
            open={() => setLocation(pipelineHealthHref)}
          />
        </div>
        {loadSalesEconomics ? (
          <Suspense
            fallback={(
              <GrowthEfficiencyOverviewCard
                analysis={null}
                companyCurrency={model.companyCurrency}
                error={null}
                href={growthEfficiencyHref}
                isLoading
                open={() => setLocation(growthEfficiencyHref)}
              />
            )}
          >
            <GrowthEfficiencyOverviewDataCard
              href={growthEfficiencyHref}
              selectedOwners={model.selectedOwners}
              selectedPipelines={model.selectedPipelines}
              selectedTeams={model.selectedTeams}
            />
          </Suspense>
        ) : (
          <GrowthEfficiencyOverviewCard
            analysis={null}
            companyCurrency={model.companyCurrency}
            error={null}
            href={growthEfficiencyHref}
            isLoading
            open={() => setLocation(growthEfficiencyHref)}
          />
        )}
      </section>
    </div>
  );
}

export function RevenueIntelligenceStateOfSalesV2LegacyForecastPage() {
  const model = useStateOfSalesV2Forecast();
  const currentUser = useAppStore(state => state.currentUser);
  const [, setLocation] = useLocation();
  const [selectedDriver, setSelectedDriver] = useState<RiskIndicator | null>(null);
  const [selectedLossSignal, setSelectedLossSignal] = useState<ForecastLossSignalGroup["group"] | null>(null);
  const [selectedConcentration, setSelectedConcentration] = useState<ForecastConcentration<ForecastDeal> | null>(null);
  const [selectedManagementAction, setSelectedManagementAction] = useState<ForecastManagementAction<ForecastDeal> | null>(null);
  const [signalsTab, setSignalsTab] = useState<"current" | "losses">("current");
  const [dependencyPage, setDependencyPage] = useState(0);

  if (model.isLoading) return <ForecastLoadingShell detail />;
  if (model.error || !model.forecast) return <ForecastUnavailable retry={model.retry} />;

  const forecast = model.forecast;
  const forecastPosition = getStateOfSalesV2ForecastPosition(
    forecast,
    model.period,
    model.accumulated,
    model.chartData,
  );
  const selectedLossSignalGroup = forecast.stateOfSalesV2LossSignalGroups.find(
    group => group.group === selectedLossSignal,
  ) ?? null;
  const contextParams = new URLSearchParams(buildStateOfSalesV2Query(model.filters));
  contextParams.set("dealDetailsViewSource", "state-of-sales-v2");
  const openDeal = (dealId: number | string) => setLocation(
    `/revenue-intelligence/deal/${dealId}?${contextParams.toString()}`,
  );

  const selectedPeriodMonths = new Set(
    model.chartData
      .filter(row => !row.is_context)
      .map(row => row.month),
  );
  const selectedPeriodOpenDeals = (
    model.expectedCloseV2Available
      ? applyExpectedCloseV2DealContributions(
        forecast.stateOfSalesV2OpenDeals,
        model.expectedCloseV2CompanyForecast,
        selectedPeriodMonths,
      )
      : forecast.stateOfSalesV2OpenDeals
  ).filter((deal) => (
    !deal.is_closed
    && !deal.is_won
    && !deal.is_lost
    && getDealForecastContribution(deal) > 0
  ));
  const urgentDeals = getManagementReviewUrgencyRisks(selectedPeriodOpenDeals)
    .map(deal => ({
      ...deal,
      topRisk: getDealTopRiskInCateogory(deal, "forecast"),
    }));
  const urgentDealById = new Map(
    urgentDeals.map(deal => [String(deal.id), deal]),
  );
  const forecastDependencyDeals = selectedPeriodOpenDeals
    .filter(deal => getDealForecastContribution(deal) > 0)
    .map((deal) => {
      const urgency = urgentDealById.get(String(deal.id));
      return {
        ...deal,
        forecast_share: forecastPosition.forecast > 0
          ? getDealForecastContribution(deal) / forecastPosition.forecast
          : null,
        topRisk: getDealTopRiskInCateogory(deal, "forecast"),
        urgency_bucket: urgency?.urgency_bucket,
        urgency_score: urgency?.urgency_score,
      };
    })
    .toSorted((a, b) => {
      const forecastImpactDifference = getDealForecastContribution(b)
        - getDealForecastContribution(a);
      if (forecastImpactDifference !== 0) return forecastImpactDifference;
      return Number(b.weighted_risk_score ?? 0) - Number(a.weighted_risk_score ?? 0);
    });
  const ownerConcentration = getTopForecastConcentration(
    forecastDependencyDeals,
    forecastPosition.forecast,
    "sales_rep",
    "Sales rep",
    (deal) => {
      const owner = getDealOwner(deal);
      return owner ? getFullName(owner) : "Not specified";
    },
  );
  const stageConcentration = getTopForecastConcentration(
    forecastDependencyDeals,
    forecastPosition.forecast,
    "stage",
    "Pipeline stage",
    (deal) => deal.stage_label ?? "Not specified",
  );
  const pipelineConcentration = getTopForecastConcentration(
    forecastDependencyDeals,
    forecastPosition.forecast,
    "pipeline",
    "Pipeline",
    (deal) => deal.pipeline_label ?? "Not specified",
  );
  const teamConcentration = getTopForecastMembershipConcentration(
    forecastDependencyDeals,
    forecastPosition.forecast,
    "team",
    "Team",
    model.teams.map(team => {
      const teamOwnerIds = new Set(getUserIdsForTeam(model.teams, team.id, true));
      return {
        dealIds: forecastDependencyDeals
          .filter((deal) => {
            const ownerId = getDealOwner(deal)?.id;
            return ownerId ? teamOwnerIds.has(ownerId) : false;
          })
          .map(deal => deal.id),
        id: team.id,
        label: team.name,
      };
    }),
  );
  const concentrations = [
    { dimension: "Sales rep", concentration: ownerConcentration },
    { dimension: "Team", concentration: teamConcentration },
    { dimension: "Pipeline stage", concentration: stageConcentration },
    { dimension: "Pipeline", concentration: pipelineConcentration },
  ].filter((item): item is { dimension: string; concentration: ForecastConcentration<ForecastDeal> } => (
    item.concentration !== null
  ));
  const currentSignalEvidence = buildForecastCurrentSignalEvidence(
    forecast.top3Risks,
    selectedPeriodOpenDeals,
    forecastPosition.forecast,
  );
  const managementSignalEvidence = buildForecastCurrentSignalEvidence(
    categoryIndicators.forecast.map(risk => ({ risk })),
    selectedPeriodOpenDeals,
    forecastPosition.forecast,
  );
  const lossPatternEvidence = buildForecastLossPatternEvidence(
    forecast.stateOfSalesV2LossSignalGroups,
    selectedPeriodOpenDeals,
  );
  const insights = buildForecastL2Insights({
    concentrations: concentrations.map(item => item.concentration),
    currentSignals: currentSignalEvidence,
    dependencyDeals: forecastDependencyDeals,
    lossPatterns: lossPatternEvidence,
    managementSignals: managementSignalEvidence,
    movement: forecastPosition.historical || model.expectedCloseV2Available
      ? null
      : forecast.stateOfSalesV2ForecastMovement,
    position: forecastPosition,
    selectedPeriodRange: forecast.stateOfSalesV2SelectedPeriodRange,
    urgentDeals,
  });
  const executiveCopy = getForecastExecutiveCopy(insights, model.companyCurrency);
  const currentPositionCopy = getCurrentPositionCopy(insights, model.companyCurrency);
  const movementCopy = getForecastMovementCopy(insights, model.companyCurrency);
  const movementDriverCopy = getForecastMovementDriverCopy(
    insights,
    model.companyCurrency,
  );
  const movementDriverCategories = [
    insights.movement.dominantPositiveDriver,
    insights.movement.dominantNegativeDriver,
  ].filter((category): category is ForecastMovementCategoryInsight => category !== null);
  const signalsCopy = getForecastSignalsCopy(insights, model.companyCurrency, signalsTab);
  const managementCopy = getForecastManagementCopy(insights, model.companyCurrency);
  const leadingManagementAction = insights.managementAttention.actions[0];
  const forecastChatContract = defineRevenueIntelligenceAnalysisContract({
    contractId: "state_of_sales.forecast",
    analysisLabel: "State of Sales Forecast",
    accessPolicy: {
      allowedRoles: ["super_admin", "admin"],
      companyId: String(currentUser?.company_id ?? "unavailable"),
      dataScope: "company",
      enforcement: "company_role_gate",
    },
    productLens: "Where the selected-period Forecast is expected to land and what needs management attention.",
    requiredAccess: "revenue_intelligence.forecast",
    sourceHelper: "buildForecastL2Insights",
    scope: {
      pageLabel: "State of Sales · Forecast",
      periodLabel: model.period,
      filtersLabel: getStateOfSalesChatFiltersLabel(model),
    },
    metrics: buildStateOfSalesForecastChatMetrics({
      companyCurrency: model.companyCurrency,
      historical: forecastPosition.historical,
      metricIdPrefix: "state_of_sales.forecast",
      position: forecastPosition,
    }),
    chat: {
      availability: "available",
      datasets: [
        buildStateOfSalesForecastTimelineChatDataset(
          model.chartData as ForecastChartRow[],
          "state_of_sales.forecast",
        ),
      ],
      topics: ["forecast", "forecast movement", "forecast risk", "prognos"],
      suggestedQuestions: [
        "What needs management attention?",
        "Where is the forecast exposed?",
        "How is Forecast calculated?",
        "What changed in the forecast?",
      ],
      insights: [
        {
          insightId: "state_of_sales.forecast.executive_summary",
          label: "Forecast position",
          title: executiveCopy.conclusion,
          body: executiveCopy.explanation ?? currentPositionCopy.supporting ?? currentPositionCopy.heading,
          evidence: executiveCopy.evidence ?? currentPositionCopy.supporting ?? currentPositionCopy.heading,
          sourceFields: ["position", "movement"],
        },
        {
          insightId: "state_of_sales.forecast.movement",
          label: movementCopy.label,
          title: movementCopy.heading,
          body: movementCopy.supporting ?? "No additional movement detail is available.",
          evidence: movementCopy.supporting ?? movementCopy.heading,
          sourceFields: ["movement"],
        },
        {
          insightId: "state_of_sales.forecast.management_attention",
          label: managementCopy.label,
          title: managementCopy.heading,
          body: managementCopy.supporting ?? "No additional management signal is available.",
          evidence: leadingManagementAction
            ? getForecastManagementActionSupportingCopy(leadingManagementAction, model.companyCurrency)
              ?? managementCopy.heading
            : managementCopy.heading,
          recommendedAction: leadingManagementAction
            ? getForecastManagementActionCopy(leadingManagementAction, model.companyCurrency)
            : undefined,
          sourceFields: ["managementAttention"],
        },
      ],
    },
  });
  const resolveChatContracts = (filters: typeof model.filters) => {
    const scopedForecast = model.evaluateForecastForFilters(filters);
    if (!scopedForecast) return [];
    const contract = buildForecastOverviewChatContract({
      accumulated: filters.accumulated,
      chartData: model.buildChartDataForFilters(
        scopedForecast,
        filters,
      ) as ForecastChartRow[],
      companyCurrency: model.companyCurrency,
      companyId: String(currentUser?.company_id ?? "unavailable"),
      filtersLabel: getStateOfSalesChatFiltersLabel({
        ...model,
        selectedOwners: filters.owners,
        selectedPipelines: filters.pipelines,
        selectedTeams: filters.teams,
      }),
      forecast: scopedForecast,
      period: filters.period,
      movementComparable: !model.expectedCloseV2Available,
    });
    const registration = getChatRegistrationFromAnalysisContract(contract);
    return registration ? [registration] : [];
  };
  const dealEvidenceCopy = getForecastDealEvidenceCopy(
    forecastDependencyDeals,
    model.companyCurrency,
  );
  const selectedDriverDeals = selectedDriver
    ? insights.currentSignals.rows.find(row => row.indicator === selectedDriver)?.deals
      ?? getDealsForForecastSignal(selectedPeriodOpenDeals, selectedDriver)
    : [];
  const selectedManagementDeals = selectedManagementAction
    ? forecastDependencyDeals.filter(deal => (
      selectedManagementAction.dealIds.includes(String(deal.id))
    ))
    : [];
  const dependencyPageSize = 20;
  const dependencyPageCount = Math.max(
    1,
    Math.ceil(forecastDependencyDeals.length / dependencyPageSize),
  );
  const safeDependencyPage = Math.min(dependencyPage, dependencyPageCount - 1);
  const visibleDependencyDeals = forecastDependencyDeals.slice(
    safeDependencyPage * dependencyPageSize,
    (safeDependencyPage + 1) * dependencyPageSize,
  );

  return (
    <div className="space-y-11 pb-8">
      <RevenueIntelligenceChatRegistration contract={forecastChatContract} />
      <RevenueIntelligenceChatScopeRegistration
        actions={buildStateOfSalesChatScopeActions(model, resolveChatContracts)}
        scopeKey="state_of_sales.filters"
      />
      <div className="space-y-5">
        <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
          <div>
            <h1 className="text-2xl font-semibold">Forecast</h1>
          </div>
          <ForecastFilters model={model} />
        </div>

        <AnalysisNarrative
          conclusion={executiveCopy.conclusion}
          explanation={executiveCopy.explanation}
          evidence={executiveCopy.evidence}
          label="Executive summary"
        />
      </div>

      <section aria-labelledby="current-forecast" className="space-y-5">
        <InsightSectionHeader
          id="current-forecast"
          label={currentPositionCopy.label}
          heading={currentPositionCopy.heading}
        />
        <Card>
          <CardContent className="p-5">
            <ForecastPosition
              position={forecastPosition}
              companyCurrency={model.companyCurrency}
              expectedLabel="Expected from open deals"
              showBar={false}
              targetUnavailable={forecastPosition.target <= 0}
            />
          </CardContent>
        </Card>
      </section>

      {!forecastPosition.historical && (
        <>
          <section aria-labelledby="forecast-movement" className="space-y-5">
            <InsightSectionHeader
              id="forecast-movement"
              label={movementCopy.label}
              heading={movementCopy.heading}
              supporting={movementCopy.supporting}
            />
            <ForecastMovementFacts companyCurrency={model.companyCurrency} insights={insights} />
            <Card>
              <CardContent className="p-5">
                {!model.expectedCloseV2Available && forecast.forecastSnapshot.available ? (
                  <ForecastMovementWaterfall
                    companyCurrency={model.companyCurrency}
                    currentForecast={forecastPosition.forecast}
                    dealsByCategory={forecast.stateOfSalesV2ForecastMovement?.dealsByCategory}
                    movementValues={forecast.stateOfSalesV2ForecastMovement?.values}
                    snapshotForecast={forecast.forecastSnapshot.forecast}
                  />
                ) : (
                  <div className="rounded-[8px] border border-dashed border-gray-200 p-5 text-sm text-gray-500">
                    {model.expectedCloseV2Available
                      ? "Comparable 30-day v2 history is not available yet."
                      : "A 30-day comparison is unavailable because the required CRM field history is incomplete."}
                  </div>
                )}
              </CardContent>
            </Card>
          </section>

          <section aria-labelledby="movement-drivers" className="space-y-5">
            <InsightSectionHeader
              id="movement-drivers"
              label={movementDriverCopy.label}
              heading={movementDriverCopy.heading}
              supporting={movementDriverCopy.supporting}
            />
            {movementDriverCategories.length ? (
              <div className={cn(
                "grid gap-4",
                movementDriverCategories.length > 1 && "md:grid-cols-2",
              )}>
                {movementDriverCategories.map(category => (
                  <MovementDriverCard
                    key={category.key}
                    category={category}
                    companyCurrency={model.companyCurrency}
                    onClickDeal={openDeal}
                  />
                ))}
              </div>
            ) : (
              <Card className="border-dashed shadow-none">
                <CardContent className="p-5 text-sm text-gray-500">
                  No deal-level movement evidence is available.
                </CardContent>
              </Card>
            )}
          </section>

          <section aria-labelledby="signals-to-monitor" className="space-y-5">
            <InsightSectionHeader
              id="signals-to-monitor"
              label={signalsCopy.label}
              heading={signalsCopy.heading}
              supporting={signalsCopy.supporting}
            />
            <Tabs value={signalsTab} onValueChange={value => setSignalsTab(value as "current" | "losses")}>
              <TabsList className="h-9">
                <TabsTrigger value="current" className="text-xs">Current exposure</TabsTrigger>
                <TabsTrigger value="losses" className="text-xs">Patterns in recent losses</TabsTrigger>
              </TabsList>
              <TabsContent value="current" className="mt-3">
                {insights.currentSignals.rows.length ? (
                  <div className="grid gap-3 md:grid-cols-3">
                    {insights.currentSignals.rows.map(evidence => (
                      <CurrentSignalCard
                        key={evidence.indicator}
                        companyCurrency={model.companyCurrency}
                        evidence={evidence}
                        onClick={() => setSelectedDriver(evidence.indicator)}
                      />
                    ))}
                  </div>
                ) : (
                  <Card className="border-dashed shadow-none">
                    <CardContent className="p-5 text-sm text-gray-500">
                      No supported current Forecast signal is available for this selection.
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
              <TabsContent value="losses" className="mt-3">
                {insights.lossPatterns.rows.some(evidence => evidence.affectedDealCount > 0) ? (
                  <div className="grid gap-3 md:grid-cols-3">
                    {insights.lossPatterns.rows.map(evidence => (
                      <LossSignalCard
                        key={evidence.group}
                        evidence={evidence}
                        onClick={() => setSelectedLossSignal(evidence.group)}
                      />
                    ))}
                  </div>
                ) : (
                  <Card className="border-dashed shadow-none">
                    <CardContent className="p-5 text-sm text-gray-500">
                      {insights.lossPatterns.state === "insufficient_signal_history"
                        ? "Signal history is unavailable for recent losses."
                        : "No significant signals were found before recent losses."}
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </section>

          <section aria-labelledby="management-attention" className="space-y-5">
            <InsightSectionHeader
              id="management-attention"
              label={managementCopy.label}
              heading={managementCopy.heading}
              supporting={managementCopy.supporting}
            />
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                {insights.managementAttention.actions.length ? (
                  insights.managementAttention.actions.map((action, index) => (
                    <ManagementActionRow
                      key={action.id ?? index}
                      action={action}
                      companyCurrency={model.companyCurrency}
                      onClick={() => {
                        if (action.dealId !== undefined) {
                          openDeal(action.dealId);
                          return;
                        }
                        if (action.concentration) {
                          setSelectedConcentration(action.concentration);
                          return;
                        }
                        if (action.type === "forecast_assumption" && action.indicator) {
                          setSelectedDriver(action.indicator);
                          return;
                        }
                        if (action.type === "worsening_outlook") {
                          document.getElementById("movement-drivers")?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                          return;
                        }
                        setSelectedManagementAction(action);
                      }}
                    />
                  ))
                ) : (
                  <p className="p-5 text-sm text-gray-500">
                    No material issue requires review for this selection.
                  </p>
                )}
              </CardContent>
            </Card>

            {concentrations.length > 0 && (
              <div className="space-y-3 pt-1">
                <p className="text-sm font-medium text-[#231942]">Organisational concentration</p>
                <div className="grid gap-3 md:grid-cols-3">
                  {concentrations.map(item => (
                    <ForecastConcentrationCard
                      key={item.dimension}
                      companyCurrency={model.companyCurrency}
                      dimension={item.dimension}
                      concentration={item.concentration}
                      onClick={() => setSelectedConcentration(item.concentration)}
                    />
                  ))}
                </div>
              </div>
            )}
          </section>

          <section aria-labelledby="deal-level-evidence" className="space-y-5">
            <InsightSectionHeader
              id="deal-level-evidence"
              label={dealEvidenceCopy.label}
              heading={dealEvidenceCopy.heading}
              supporting={dealEvidenceCopy.supporting}
            />
            {forecastDependencyDeals.length ? (
              <>
                <div className="max-h-[62vh] min-w-0 max-w-full overflow-y-auto">
                  <AtRiskDealsDataTable
                    deals={visibleDependencyDeals}
                    companyCurrency={model.companyCurrency}
                    displayMode="key-forecast-deals"
                    showTitle={false}
                    showTopRisk
                    showExpectedCloseDate
                    showForecastImpact
                    forecastValueDisplay="contribution"
                    showForecastShare
                    showUrgency
                    topRiskDisplay="primary-reason"
                    onClickDeal={openDeal}
                  />
                </div>
                {dependencyPageCount > 1 && (
                  <div className="flex items-center justify-between gap-3 text-xs text-gray-500">
                    <span>Page {safeDependencyPage + 1} of {dependencyPageCount}</span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={safeDependencyPage === 0}
                        onClick={() => setDependencyPage(Math.max(0, safeDependencyPage - 1))}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={safeDependencyPage >= dependencyPageCount - 1}
                        onClick={() => setDependencyPage(Math.min(dependencyPageCount - 1, safeDependencyPage + 1))}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <Card className="mt-3 border-dashed shadow-none">
                <CardContent className="p-5 text-sm text-gray-500">
                  No selected-period open deal has positive Forecast contribution.
                </CardContent>
              </Card>
            )}
          </section>
        </>
      )}

      <Dialog open={selectedLossSignal !== null} onOpenChange={(open) => !open && setSelectedLossSignal(null)}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>{selectedLossSignalGroup?.label ?? "Recent-loss signals"}</DialogTitle>
            <DialogDescription>
              {selectedLossSignalGroup
                ? `${selectedLossSignalGroup.affectedDealCount} of ${selectedLossSignalGroup.recentLostDealCount} recent lost deals had a Medium or High signal in this group before close.`
                : "Recent lost deals with a Medium or High signal in this group before close."}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto">
            <AtRiskDealsDataTable
              deals={(selectedLossSignalGroup?.deals ?? []).toSorted(
                (a, b) => new Date(b.closed_at ?? 0).getTime() - new Date(a.closed_at ?? 0).getTime(),
              )}
              displayMode="recent-loss-signals"
              showTitle={false}
              onClickDeal={openDeal}
            />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={selectedDriver !== null} onOpenChange={(open) => !open && setSelectedDriver(null)}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>{selectedDriver ? mapRiskName(selectedDriver) : "Forecast driver"}</DialogTitle>
            <DialogDescription>
              {selectedDriver
                ? `${selectedDriverDeals.length} selected-period ${selectedDriverDeals.length === 1 ? "deal" : "deals"}, highest signal score first.`
                : "Selected-period deals, highest signal score first."}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto">
            <AtRiskDealsDataTable
              deals={selectedDriverDeals}
              showTitle={false}
              highlightIndicator={selectedDriver ?? undefined}
              hideRiskColumn
              showExpectedCloseDate
              onClickDeal={openDeal}
            />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={selectedConcentration !== null} onOpenChange={(open) => !open && setSelectedConcentration(null)}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>{selectedConcentration?.label ?? "Forecast concentration"}</DialogTitle>
            <DialogDescription>
              {selectedConcentration
                ? `${formatForecastShare(selectedConcentration.share)} of Forecast in ${selectedConcentration.dimension.toLowerCase()}, across ${selectedConcentration.dealCount} ${selectedConcentration.dealCount === 1 ? "deal" : "deals"}.`
                : "Deals included in this Forecast concentration."}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto">
            <AtRiskDealsDataTable
              deals={selectedConcentration?.deals ?? []}
              showTitle={false}
              showTopRisk
              showExpectedCloseDate
              showForecastImpact
              forecastValueDisplay="contribution"
              showForecastShare
              topRiskDisplay="primary-reason"
              onClickDeal={openDeal}
            />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={selectedManagementAction !== null} onOpenChange={(open) => !open && setSelectedManagementAction(null)}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>Late-period dependency</DialogTitle>
            <DialogDescription>
              Open deals expected to close during the final two weeks of the selected period.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto">
            <AtRiskDealsDataTable
              deals={selectedManagementDeals}
              companyCurrency={model.companyCurrency}
              displayMode="key-forecast-deals"
              showTitle={false}
              showTopRisk
              showExpectedCloseDate
              showForecastImpact
              forecastValueDisplay="contribution"
              showForecastShare
              showUrgency
              topRiskDisplay="primary-reason"
              onClickDeal={openDeal}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function StateOfSalesV2DeferredCategoryPage({ title }: { title: string }) {
  const returnHref = typeof window === "undefined"
    ? "/state-of-sales"
    : `/state-of-sales${window.location.search}`;

  return (
    <div className="space-y-5 pb-8">
      <Card className="border-dashed">
        <CardContent className="flex min-h-80 flex-col items-center justify-center gap-3 text-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-[#5E548E]/10 text-[#5E548E]">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <h1 className="text-xl font-semibold">{title}</h1>
            <p className="mt-2 max-w-md text-sm text-gray-500">
              Detailed {title.toLowerCase()} analysis is not available yet.
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link href={returnHref}>Return to overview</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
